import React, { useEffect, useState } from "react";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import PredefinedDateRanges from "../../../core/common/datePicker";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { uploadFile } from "../../../apis/auth";
import CommonSearch from "../commonSearch";
import { Moment } from "moment";
import {
  checkVerificationsById,
  updateVerificationsById,
} from "../../../apis/verification";
import { successAlert } from "../../../style/scss/utils/Alerts";
import { formatDate } from "../../../environment";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    bootstrap?: any;
  }
}

const SecondVerificationList = () => {
  const initialState = {
    verificationType: "",
    remark: "",
    status: "verified",
  };

  const [allEmployeeData, setEmployeeData] = useState<EmployeeData[]>([]);
  const [filteredData, setFilteredData] = useState<EmployeeData[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [verificationId, setVerificationId] = useState();
  const [formNewData, setFormNewData] = useState(initialState);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [states, setState] = useState<{
    start: Moment | undefined;
    end: Moment | undefined;
  }>({
    start: undefined,
    end: undefined,
  });
  const { start, end } = states;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Image size should be below 10 MB");
        setImage(null);
        return;
      }

      setError("");
      setImage(file);
      const formData = new FormData();
      formData.append("image", file);
      const response = await uploadFile(formData);
      if (response.status === true) {
        setImageUrl(response.data.image);
      }
    }
  };

  type EmployeeData = {
    key: number;
    EmpId: string;
    Name: string;
    Image: string;
    CurrentRole: string;
    Email: string;
    Phone: string;
    emp_name: string;
    emp_type: string;
    emp_email: string;
    Designation: string;
    JoiningDate: string;
    Status: string;
    id: any;
    allData: any;
    createdAt: string;
    updatedAt: string;
  };
  const fetchAllEmployees = async () => {
    try {
      const mtIdString = sessionStorage.getItem("info");
      let mtId: { id: string; type: string } | null = null;

      if (mtIdString) {
        try {
          mtId = JSON.parse(mtIdString);
        } catch (error) {
          console.error("Failed to parse sessionStorage info:", error);
        }
      }

      const data = {
        id: mtId?.id,
        type: mtId?.type,
        start_date: start ? start.format("MM/DD/YYYY") : undefined,
        end_date: end ? end.format("MM/DD/YYYY") : undefined,
      };
      const response = await checkVerificationsById(data);
      const employeeData = response?.data?.map((item: any, i: number) => ({
        key: `${i + 1}`,
        EmpId: `${i + 1}`,
        Name: `${item.customer?.full_name}`,
        emp_name: `${item.employees?.full_name}`,
        emp_type: `${item.employees?.type}`,
        emp_email: `${item.employees?.email}`,
        Image: item.Image ? item.Image : "dummy",
        CurrentRole: item.CurrentRole || "--",
        Email: item?.customer?.email || "--",
        Status: item.status || "--",
        id: item.id || "--",
        allData: item,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
      setEmployeeData(employeeData || []);
      setFilteredData(employeeData || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };
  const handleCancel = () => {
    setImage(null);
    setError("");
  };
  useEffect(() => {
    fetchAllEmployees();
  }, [states]);

  const columns = [
    {
      title: "Sr No",
      dataIndex: "key",
      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "Customer Account Number",
      dataIndex: "customerAccountNumber",
      render: (_: any, record: EmployeeData) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              {record?.allData?.customer?.loanAccountNumber}
            </p>
          </div>
        </div>
      ),
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.Name.localeCompare(b.Name),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (_: any, record: EmployeeData) => (
        <div>{formatDate(record.createdAt)}</div>
      ),
    },
    {
      title: "Verify Date",
      dataIndex: "updatedAt",
      render: (_: any, record: EmployeeData) => (
        <div>
          {" "}
          {record.Status === "verified" ? formatDate(record.updatedAt) : "--"}
        </div>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "Name",
      render: (_: any, record: EmployeeData) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">{record.Name}</p>
          </div>
        </div>
      ),
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.Name.localeCompare(b.Name),
    },
    {
      title: "Customer Email",
      dataIndex: "Email",
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.Email.localeCompare(b.Email),
    },
    {
      title: "Emp. Name",
      dataIndex: "emp_name",
      render: (_: any, record: EmployeeData) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">{record.emp_name}</p>
          </div>
        </div>
      ),
    },
    {
      title: "Emp. Email",
      dataIndex: "emp_email",
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.emp_email.localeCompare(b.emp_email),
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => (
        <span className="dropdown">
          <div
            className={`badge  btn btn-white ${text === "Active" ? "badge-success" : "badge-danger"
              } d-inline-flex align-items-center badge-xs`}
          >
            <i className="ti ti-point-filled me-1" />
            {text}
          </div>
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: EmployeeData) => (
        <>
          <div className="action-icon d-inline-flex">
            <Link
              to={`/viewVerification?EmployeeData=${encodeURIComponent(
                JSON.stringify(record.allData)
              )}`}
            >
              <i className="ti ti-eye" />
            </Link>
          </div>
          <div className="d-inline-flex">
            {record?.Status !== "verified" ? (
              <div
                data-bs-toggle="modal"
                data-inert={true}
                data-bs-target="#visits"
                className="btn btn-primary d-flex align-items-center"
                onClick={() => handleVisitClick(record)}
              >
                {"Verify"}
              </div>
            ) : (
              <div className="btn btn-primary d-flex align-items-center">
                {"Visited"}
              </div>
            )}
          </div>
        </>
      ),
    },
    {
      title: "Customer Details",
      dataIndex: "actions",
      render: (text: string, record: EmployeeData) => (
        <Link
          to={`/customer-view?id=${record?.allData.customer?.id}`}
          className="btn btn-primary d-flex align-items-center"
        >
          {"Details"}
        </Link>
      ),
    },
  ];

  const [geoLocation, setGeoLocation] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  const handleVisitClick = (record: EmployeeData) => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setGeoLocation({ lat: latitude, lon: longitude });
        setVerificationId(record?.id);
        setModalOpen(true);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          window.ReactNativeWebView?.postMessage("ASK_FOR_LOCATION_PERMISSION");
          setModalOpen(false);
          alert("Please enable location access to proceed.");
        } else {
          alert("Unable to retrieve location. Please try again.");
        }
      }
    );
  };

  const mtIdString = sessionStorage.getItem("info");
  let mtId: { id: string } | null = null;

  if (mtIdString) {
    try {
      mtId = JSON.parse(mtIdString);
    } catch (error) {
      console.error("Failed to parse sessionStorage info:", error);
    }
  }

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(allEmployeeData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "EmployeeData");
    XLSX.writeFile(workbook, "employee_data.xlsx");
  };

  // const [allEmployeesData, setEmployeesData] = useState<EmployeeData[]>([]);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormNewData({ ...formNewData, [name]: value });
  };
  // const getAllEmployees = async () => {
  //   try {
  //     // const data = {
  //     //   start_date: "",
  //     //   end_date: "",
  //     // };
  //     // const response = await getAllEmployee(data);
  //     // const filteredEmployees = response.data.filter(
  //     //   (employee: any, i: any) => employee.type === "verfication_person"
  //     // );
  //     // const employeeData = filteredEmployees?.map((item: any, i: number) => ({
  //     //   label: item.full_name ? item.full_name : "Not Assigned",
  //     //   value: item.id || "Not Assigned",
  //     // }));
  //     // setEmployeesData(employeeData);
  //   } catch (error) {
  //     console.error(error, "error");
  //   }
  // };

  // useEffect(() => {
  //   getAllEmployees();
  // }, []);
  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!geoLocation) {
      alert("Please allow location access to proceed.");
      return;
    }
    const payload = {
      ...formNewData,
      staffVerificationImage: imageUrl,
      geo_location: geoLocation,
    };
    try {
      const response = await updateVerificationsById(verificationId, payload);
      if (response.status === true) {
        successAlert("success", "Verification Successfully Updated");
        setImage(null);
        setError("");
        setFormNewData(initialState);
        fetchAllEmployees();
      }
    } catch (error) {
    } finally {
    }
  };
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2"></div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2 mb-2"></div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="me-3">
                  <CommonSearch
                    allEmployeeData={allEmployeeData}
                    setFilteredData={setFilteredData}
                  />
                </div>
                <div className="input-icon-end position-relative">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    <label style={{ color: "#000" }}>Date Range</label>
                    <PredefinedDateRanges state={states} setState={setState} />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    margin: "20px 0px 0px 6rem",
                  }}
                >
                  <div className="dropdown me-3">
                    <Link
                      style={{
                        background: "#3ab54bbf",
                        color: "#fff",
                      }}
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      Export
                    </Link>

                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <div
                          onClick={exportToExcel}
                          className=" btn  dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {filteredData && filteredData.length > 0 && (
                <Table
                  dataSource={filteredData}
                  columns={columns}
                  Selection={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="visits">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title me-2">Add Visitor</h4>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            {modalOpen ? (
              <form onSubmit={handleSubmit}>
                <div className="contact-grids-tab">
                  <ul className="nav nav-underline" id="myTab2" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="info-tab2"
                        data-bs-toggle="tab"
                        data-bs-target="#basic-info2"
                        type="button"
                        role="tab"
                        aria-selected="true"
                      >
                        Basic Information
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="tab-content" id="myTabContent2">
                  <div
                    className="tab-pane fade show active"
                    id="basic-info2"
                    role="tabpanel"
                    aria-labelledby="info-tab2"
                    tabIndex={0}
                  >
                    <div className="modal-body pb-0 ">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Employee Verification Type
                            </label>
                            <input
                              type="text"
                              onChange={handleChange}
                              value={formNewData.verificationType}
                              name="verificationType"
                              className="form-control"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Remark</label>
                            <input
                              type="text"
                              onChange={handleChange}
                              value={formNewData.remark}
                              name="remark"
                              className="form-control"
                            />
                          </div>
                        </div>
                        <div className="profile-upload">
                          <div className="mb-2">
                            <h6 className="mb-1">
                              Upload Staff Verification Image
                            </h6>
                            <p className="fs-12">Image should be below 4 MB</p>
                            {error && <p className="text-danger">{error}</p>}
                          </div>
                          <div className="profile-uploader d-flex align-items-center">
                            <div className="drag-upload-btn btn btn-sm btn-primary me-2">
                              {image ? "Replace" : "Upload"}
                              <input
                                type="file"
                                className="form-control image-sign"
                                accept="image/*"
                                onChange={handleFileChange}
                              />
                            </div>
                            <Link
                              to="#"
                              className="btn btn-light btn-sm"
                              onClick={handleCancel}
                            >
                              Cancel
                            </Link>
                          </div>
                          {image && (
                            <div className="mt-3">
                              <p>Preview:</p>
                              <img
                                src={URL.createObjectURL(image)}
                                alt="Profile Preview"
                                className="img-thumbnail"
                                style={{
                                  maxWidth: "150px",
                                  maxHeight: "150px",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-light border me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      {imageUrl == "" ? (
                        ""
                      ) : (
                        <button
                          type="submit"
                          data-bs-dismiss="modal"
                          className="btn btn-primary"
                        >
                          Submit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <h4>Please Allow the location first</h4>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SecondVerificationList;
